import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCart, resetCart } from "~/hooks/useCart";
import { invoke } from "~/server/invoke";
import { getCookie, setCookie } from "~/utils/cookies";
import { parseSharedOrderFormId, SHARED_CART_SESSION_FLAG } from "~/utils/shareCart";
import Modal from "~/components/ui/Modal";
import Button from "~/components/ui/Button";

const ORDER_FORM_COOKIE_NAME = "checkout.vtex.com__orderFormId";
const ORDER_FORM_COOKIE_MAX_AGE = 7 * 24 * 3600;

export const Route = createFileRoute("/carrinho-compartilhado")({
  component: SharedCartPage,
});

type Status = "validating" | "confirm" | "invalid" | "switching";

function SharedCartPage() {
  const search = Route.useSearch() as Record<string, unknown>;
  const navigate = useNavigate();
  const { cart, loading, initFailed } = useCart();
  const [status, setStatus] = useState<Status>("validating");
  const [validated, setValidated] = useState(false);

  const orderFormId = parseSharedOrderFormId(search);

  // Passo 1: valida que o orderFormId compartilhado existe de fato na VTEX.
  // Só marca `validated`; a decisão confirm/switching fica pro efeito abaixo,
  // que também espera o próprio carrinho (useCart) terminar de carregar.
  useEffect(() => {
    if (!orderFormId) {
      setStatus("invalid");
      return;
    }

    let cancelled = false;
    invoke.vtex.actions.getOrCreateCart({ data: { orderFormId } })
      .then(() => {
        if (!cancelled) setValidated(true);
      })
      .catch(() => {
        if (!cancelled) setStatus("invalid");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFormId]);

  // Passo 2: decide entre "confirm" (carrinho próprio já tem itens) e
  // "switching" (carrinho próprio vazio, troca direto) -- só depois que a
  // validação acima terminou E o `useCart()` deste componente já concluiu
  // sua própria inicialização (`loading.value === false`). Sem esperar por
  // `loading`, num load frio (link aberto direto/incógnito) a validação do
  // carrinho compartilhado pode resolver antes do `getOrCreateCart` interno
  // do `useCart()`, e `cart.value` ainda estaria `null` mesmo que o
  // visitante já tivesse um carrinho salvo (cookie) com itens -- pulando a
  // confirmação indevidamente.
  useEffect(() => {
    if (status !== "validating" || !validated) return;
    if (loading.value) return;

    // Se o carregamento do carrinho PRÓPRIO falhou (ex.: 503 transitório da
    // VTEX -- caso documentado no próprio `useCart()`), `cart.value` fica
    // `null` sem que isso signifique "carrinho vazio": não sabemos de fato
    // se o visitante tinha itens. Tratar como "pode ter" (pede confirmação)
    // em vez de assumir vazio e trocar direto -- mais seguro que arriscar
    // sobrescrever um carrinho que só não carregou.
    if (initFailed.value) {
      setStatus("confirm");
      return;
    }

    const ownItemsCount = cart.value?.items?.length ?? 0;
    setStatus(ownItemsCount > 0 ? "confirm" : "switching");
  }, [status, validated, loading.value, initFailed.value, cart.value]);

  useEffect(() => {
    if (status !== "switching" || !orderFormId) return;
    switchToSharedCart(orderFormId);
  }, [status, orderFormId]);

  // A validação (Passo 1, acima) chama `getOrCreateCart` com o orderFormId
  // COMPARTILHADO. Esse `invoke` passa pela `vtexFetchWithCookies`, que
  // encaminha os `Set-Cookie` da resposta da VTEX de volta pro navegador
  // (`forwardResponseCookies` / `cookieBridge.ts`) -- e um
  // `GET /api/checkout/pub/orderForm/<id>` responde com
  // `checkout.vtex.com=__ofid=<id>`, o cookie NATIVO de sessão de checkout da
  // VTEX (diferente do `checkout.vtex.com__orderFormId` que este app
  // gerencia). Ou seja: só de validar que o link existe, a sessão nativa de
  // checkout do navegador já fica apontando pro carrinho compartilhado --
  // mesmo que o visitante ainda não tenha confirmado nada. Se ele clicar
  // "Cancelar" (ou fechar o modal) e for direto pro checkout sem antes fazer
  // qualquer outra ação no carrinho, a VTEX poderia resolver pro carrinho
  // compartilhado em vez do próprio. Por isso, ao cancelar, re-emitimos a
  // mesma chamada com o orderFormId PRÓPRIO do visitante, pra devolver o
  // cookie nativo pro carrinho dele antes de navegar embora. Best-effort: se
  // não tivermos o orderFormId próprio ainda, ou a chamada falhar, não há
  // nada a restaurar -- não bloqueia a navegação.
  function restoreOwnCheckoutSessionAndLeave() {
    // Fallback pro cookie: no caminho `initFailed` (o carregamento do carrinho
    // próprio falhou) `cart.value` é `null` -- justamente o cenário que este
    // modal existe pra proteger. Sem o fallback, "Cancelar" não restauraria
    // nada e o cookie nativo da VTEX ficaria apontando pro carrinho compartilhado.
    const ownOrderFormId = cart.value?.orderFormId ?? getCookie(ORDER_FORM_COOKIE_NAME);
    if (ownOrderFormId) {
      invoke.vtex.actions
        .getOrCreateCart({ data: { orderFormId: ownOrderFormId } })
        .catch(() => {
          // Best-effort -- segue a navegação normalmente.
        });
    }
    navigate({ to: "/" });
  }

  function switchToSharedCart(id: string) {
    setCookie(ORDER_FORM_COOKIE_NAME, id, ORDER_FORM_COOKIE_MAX_AGE);
    // Guarda o id do carrinho compartilhado (não um "1"): o mini-cart só mostra
    // o banner enquanto o carrinho ativo ainda for este. Se o visitante depois
    // montar um carrinho próprio, o id deixa de bater e o banner some.
    sessionStorage.setItem(SHARED_CART_SESSION_FLAG, id);
    resetCart();
    // Hard reload: garante que o `useCart()` do header/mini-cart, já montado,
    // reinicialize lendo o cookie novo -- um <Navigate> por SPA não remonta
    // esse singleton.
    globalThis.location.href = "/";
  }

  if (status === "invalid") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6 text-center">
        <p>Este link de carrinho compartilhado não é válido ou expirou.</p>
        <Button className="btn-outline" onClick={() => navigate({ to: "/" })}>
          Ir para a loja
        </Button>
      </div>
    );
  }

  if (status === "confirm" && orderFormId) {
    return (
      <Modal open onClose={restoreOwnCheckoutSessionAndLeave}>
        <div className="modal-box flex flex-col gap-4">
          {/* Redação conservadora de propósito: este modal também aparece no
              caminho `initFailed`, quando NÃO sabemos se o visitante tem itens
              -- afirmar "você tem itens no seu carrinho" seria falso ali. */}
          <p>
            Isso pode substituir o conteúdo do seu carrinho atual pelo carrinho
            compartilhado. Continuar?
          </p>
          <div className="flex gap-2 justify-end">
            <Button className="btn-ghost" onClick={restoreOwnCheckoutSessionAndLeave}>
              Cancelar
            </Button>
            <Button
              className="btn-primary"
              onClick={() => setStatus("switching")}
            >
              Continuar
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // "validating" / "switching": esta rota não tem header nem footer (o layout
  // raiz só renderiza <Outlet/> pras rotas de código), então sem isto o
  // visitante encara uma página em branco durante duas idas e voltas de rede
  // (validar o link + carregar o carrinho próprio) -- 1-3s no mobile, o que lê
  // como site quebrado.
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6 text-center">
      <span className="loading loading-spinner" />
      <p>Abrindo carrinho compartilhado…</p>
    </div>
  );
}
